import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import type { MeshData } from './core/types';

export type ViewDirection = 'iso' | 'front' | 'top';

const BG = 0x10141a;
const OPEN_COLOR = 0xff4d4d;
const NON_MANIFOLD_COLOR = 0xff4fd8;

export class Viewer {
  private container: HTMLElement;
  private renderer: THREE.WebGLRenderer;
  private scene: THREE.Scene;
  private camera: THREE.PerspectiveCamera;
  private controls: OrbitControls;
  private resizeObs: ResizeObserver;
  private raf = 0;

  private modelGroup = new THREE.Group();
  private currentMesh: THREE.Mesh | null = null;
  private wireframe: THREE.LineSegments | null = null;
  private openLines: THREE.LineSegments | null = null;
  private nonManifoldLines: THREE.LineSegments | null = null;
  private wireWanted = false;
  private issuesWanted = true;
  private radius = 100;

  private material = new THREE.MeshStandardMaterial({
    color: 0xb9c7d8,
    metalness: 0.12,
    roughness: 0.62,
    side: THREE.DoubleSide,
    flatShading: true,
  });

  constructor(container: HTMLElement) {
    this.container = container;

    this.renderer = new THREE.WebGLRenderer({ antialias: true });
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    container.appendChild(this.renderer.domElement);

    this.scene = new THREE.Scene();
    this.scene.background = new THREE.Color(BG);

    this.camera = new THREE.PerspectiveCamera(45, 1, 0.1, 100000);
    this.camera.position.set(120, 160, 140);

    this.controls = new OrbitControls(this.camera, this.renderer.domElement);
    this.controls.enableDamping = true;
    this.controls.dampingFactor = 0.09;
    this.controls.screenSpacePanning = true;

    this.scene.add(new THREE.HemisphereLight(0xffffff, 0x2a3240, 1.1));
    const key = new THREE.DirectionalLight(0xffffff, 1.45);
    key.position.set(1, 1.6, 1.2);
    this.scene.add(key);
    const fill = new THREE.DirectionalLight(0xffffff, 0.35);
    fill.position.set(-1, -0.7, -1);
    this.scene.add(fill);

    this.scene.add(this.modelGroup);

    this.resizeObs = new ResizeObserver(() => this.resize());
    this.resizeObs.observe(container);
    this.resize();
    this.loop();
  }

  /**
   * Pokazuje siatkę. `openEdges` i `nonManifoldEdges` to płaskie tablice
   * 6 floatów na odcinek (ax, ay, az, bx, by, bz).
   */
  setMesh(
    mesh: MeshData,
    openEdges: Float32Array,
    nonManifoldEdges: Float32Array,
    fit = false,
  ): void {
    this.clearModel();

    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.BufferAttribute(mesh.positions, 3));
    geometry.setIndex(new THREE.BufferAttribute(mesh.indices, 1));
    geometry.computeVertexNormals();
    geometry.computeBoundingBox();

    const box = geometry.boundingBox ?? new THREE.Box3();
    const center = box.getCenter(new THREE.Vector3());
    const size = box.getSize(new THREE.Vector3());
    this.radius = Math.max(size.x, size.y, size.z) / 2 || 1;

    this.modelGroup.position.set(-center.x, -center.y, -center.z);

    const surface = new THREE.Mesh(geometry, this.material.clone());
    this.currentMesh = surface;
    this.modelGroup.add(surface);

    if (openEdges.length > 0) {
      this.openLines = this.makeLines(openEdges, OPEN_COLOR);
      this.openLines.visible = this.issuesWanted;
      this.modelGroup.add(this.openLines);
    }

    if (nonManifoldEdges.length > 0) {
      this.nonManifoldLines = this.makeLines(nonManifoldEdges, NON_MANIFOLD_COLOR);
      this.nonManifoldLines.visible = this.issuesWanted;
      this.modelGroup.add(this.nonManifoldLines);
    }

    if (this.wireWanted) this.buildWireframe(geometry);

    if (fit) this.fit();
    else this.controls.update();
  }

  setWireframe(on: boolean): void {
    this.wireWanted = on;
    if (!on) {
      if (this.wireframe) this.wireframe.visible = false;
      return;
    }
    if (this.wireframe) {
      this.wireframe.visible = true;
      return;
    }
    if (this.currentMesh) this.buildWireframe(this.currentMesh.geometry as THREE.BufferGeometry);
  }

  setIssues(on: boolean): void {
    this.issuesWanted = on;
    if (this.openLines) this.openLines.visible = on;
    if (this.nonManifoldLines) this.nonManifoldLines.visible = on;
  }

  fit(): void {
    const fov = (this.camera.fov * Math.PI) / 180;
    const distance = (this.radius / Math.tan(fov / 2)) * 1.25;
    this.camera.position.set(distance * 0.85, distance * 1.15, distance * 0.95);
    this.camera.up.set(0, 1, 0);
    this.camera.near = Math.max(distance / 1000, 0.001);
    this.camera.far = distance * 200;
    this.camera.updateProjectionMatrix();
    this.controls.target.set(0, 0, 0);
    this.controls.update();
  }

  setViewDirection(direction: ViewDirection): void {
    const fov = (this.camera.fov * Math.PI) / 180;
    const distance = (this.radius / Math.tan(fov / 2)) * 1.35;

    this.camera.up.set(0, 1, 0);
    if (direction === 'front') {
      this.camera.position.set(0, 0, distance);
    } else if (direction === 'top') {
      this.camera.up.set(0, 0, 1);
      this.camera.position.set(0, distance, 0);
    } else {
      this.camera.position.set(distance * 0.8, distance * 1.05, distance * 0.85);
    }

    this.controls.target.set(0, 0, 0);
    this.controls.update();
  }

  private makeLines(data: Float32Array, color: number): THREE.LineSegments {
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.BufferAttribute(data, 3));
    const material = new THREE.LineBasicMaterial({
      color,
      depthTest: false,
      transparent: true,
      opacity: 0.92,
    });
    const lines = new THREE.LineSegments(geometry, material);
    lines.renderOrder = 10;
    return lines;
  }

  private buildWireframe(geometry: THREE.BufferGeometry): void {
    if (this.wireframe) {
      this.modelGroup.remove(this.wireframe);
      this.wireframe.geometry.dispose();
      (this.wireframe.material as THREE.Material).dispose();
      this.wireframe = null;
    }
    const wire = new THREE.WireframeGeometry(geometry);
    const material = new THREE.LineBasicMaterial({
      color: 0x6f7f91,
      transparent: true,
      opacity: 0.28,
      depthTest: true,
    });
    this.wireframe = new THREE.LineSegments(wire, material);
    this.wireframe.visible = this.wireWanted;
    this.modelGroup.add(this.wireframe);
  }

  private clearModel(): void {
    for (const child of [...this.modelGroup.children]) {
      this.modelGroup.remove(child);
      child.traverse((object) => {
        const mesh = object as THREE.Mesh;
        if (mesh.geometry) mesh.geometry.dispose();
        const material = mesh.material as THREE.Material | THREE.Material[] | undefined;
        if (Array.isArray(material)) material.forEach((m) => m.dispose());
        else material?.dispose();
      });
    }
    this.currentMesh = null;
    this.wireframe = null;
    this.openLines = null;
    this.nonManifoldLines = null;
    this.modelGroup.position.set(0, 0, 0);
  }

  private resize(): void {
    const width = this.container.clientWidth || 1;
    const height = this.container.clientHeight || 1;
    this.renderer.setSize(width, height);
    this.camera.aspect = width / height;
    this.camera.updateProjectionMatrix();
  }

  private loop = (): void => {
    this.raf = requestAnimationFrame(this.loop);
    this.controls.update();
    this.renderer.render(this.scene, this.camera);
  };

  dispose(): void {
    cancelAnimationFrame(this.raf);
    this.resizeObs.disconnect();
    this.controls.dispose();
    this.clearModel();
    this.renderer.dispose();
  }
}
